import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { DashboardBaseComponent } from '../../relatorios/dashboard-operacional/dashboard-base';
import {
  KpiCard,
  PieSlice
} from '../../relatorios/dashboard-operacional/dashboard-operacional.mock';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import {
  IDashboardMovimentacao,
  IGetDashboardEstoqueResponse
} from 'src/app/services/EstoqueService/estoque.model';
import { StorageService } from 'src/app/services/StorageService/storage.service';
import { labelOrigemMovimentacao } from '../estoque.constants';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import { IOpcaoTela } from 'src/app/core/utils/permissionScreen/permissionScreen.types';

@Component({
  selector: 'app-dashboard-estoque',
  templateUrl: './dashboard-estoque.component.html',
  styleUrls: ['./dashboard-estoque.component.scss']
})
export class DashboardEstoqueComponent extends DashboardBaseComponent implements OnInit {
  readonly dashboardTitle = 'Dashboard de Estoque';
  readonly pdfFileNamePrefix = 'Dashboard-Estoque';
  readonly dashboardStorageKey = 'dashboard-estoque-sections';

  kpis: KpiCard[] = [];
  vendasPorMes: { label: string; value: number }[] = [];
  quantidadePorProdutoVendido: { label: string; value: number }[] = [];
  quantidadePorProdutoEstoque: { label: string; value: number }[] = [];
  movimentacoesPorOrigem: { label: string; value: number }[] = [];
  formaPagamento: PieSlice[] = [];
  formaPagamentoDetalhe: { label: string; quantidade: number; valor: number; color: string }[] = [];

  constructor(
    fb: FormBuilder,
    storageService: StorageService,
    private estoqueService: EstoqueService
  ) {
    super(fb, storageService);
    const fim = moment().endOf('day');
    const inicio = moment().subtract(3, 'months').startOf('month');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  podeVerGrafico(tela: IOpcaoTela): boolean {
    return validarPermissao(tela, 'consultar');
  }

  ngOnInit(): void {
    this.initSectionCollapse({
      kpis: false,
      vendasMes: false,
      produtos: false,
      payment: false,
      movimentacoes: false
    });
    this.loadData();
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  private loadData(): void {
    this.isLoading = true;
    this.estoqueService
      .GetDashboard({
        DT_INIC: (this.form.controls['DT_INIC'].value as string | null) ?? undefined,
        DT_FIM: (this.form.controls['DT_FIM'].value as string | null) ?? undefined
      })
      .subscribe({
        next: data => this.applyResponse(data),
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private applyResponse(data: IGetDashboardEstoqueResponse): void {
    if (!data) {
      this.isLoading = false;
      return;
    }

    this.kpis = [
      {
        label: 'Produtos ativos',
        value: String(data.QT_PROD_ATV),
        hint: 'Com status ativo'
      },
      {
        label: 'Quantidade em estoque',
        value: String(data.QT_ESTQ_TOTL ?? 0),
        hint: 'Soma dos saldos'
      },
      {
        label: 'Estoque baixo',
        value: String(data.QT_PROD_ESTQ_BX),
        hint: 'Abaixo do mínimo'
      },
      {
        label: 'Valor em estoque',
        value: this.formatCurrency(data.VL_ESTQ_TOTL),
        hint: 'Saldo × valor unitário'
      },
      {
        label: 'Vendas no período',
        value: this.formatCurrency(data.VL_VEND_TOTL),
        hint: 'Total das vendas em R$'
      },
      {
        label: 'Qtd. produtos vendidos',
        value: String(data.QT_VEND_TOTL ?? 0),
        hint: 'Soma das quantidades'
      }
    ];

    this.vendasPorMes = (data.VendasPorMes ?? []).map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));

    this.quantidadePorProdutoVendido = (data.QuantidadePorProdutoVendido ?? []).map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));

    this.quantidadePorProdutoEstoque = (data.QuantidadePorProdutoEstoque ?? []).map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));

    this.formaPagamentoDetalhe = (data.FormaPagamento ?? []).map(item => ({
      label: item.Label,
      quantidade: Number(item.Quantidade) || 0,
      valor: Number(item.Valor) || 0,
      color: item.Color || '#94a3b8'
    }));

    this.formaPagamento = this.formaPagamentoDetalhe.map(item => ({
      label: item.label,
      value: item.valor,
      color: item.color
    }));

    this.applyMovimentacoesPorOrigem(
      data.QuantidadePorOrigemMovimentacao,
      data.UltimasMovimentacoes ?? []
    );
    this.isLoading = false;
  }

  private applyMovimentacoesPorOrigem(
    aggregated: { Label: string; Value: number }[] | undefined,
    fallbackItems: IDashboardMovimentacao[]
  ): void {
    if (aggregated?.length) {
      this.movimentacoesPorOrigem = aggregated.map(item => ({
        label: item.Label,
        value: Number(item.Value) || 0
      }));
      return;
    }

    const byOrigem = new Map<string, number>();
    for (const item of fallbackItems) {
      const origem = labelOrigemMovimentacao(item.NU_ORIG_MOVM);
      const qty = Math.abs(Number(item.QT_MOVM) || 0);
      byOrigem.set(origem, (byOrigem.get(origem) ?? 0) + qty);
    }

    this.movimentacoesPorOrigem = [...byOrigem.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }
}
